import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Link from '@mui/material/Link';
import { useFeature } from 'flagged';
import CustomDialog, {
  DialogActions,
  DialogCancelButton,
  DialogHeader,
} from '@/components/common/CustomDialog';
import SelectAllCheckboxesOrNone from '@/components/common/form/SelectAllCheckboxesOrNone';
import ConstituentExportFieldName from '@/constants/constituents/ConstituentExportFieldName';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import FeatureFlags from '@/constants/system/FeatureFlags';
import useMobileView from '@/hooks/ui/useMobileView';
import { useCurrentUser } from '@/lib/hooks/userData';
import translate from '@/lib/intl/translate';
import type ConstituentExportFilters from '@/types/api/ConstituentExportFilters';
import CsvExportCustomFields from '../constants/CsvExportCustomFields';
import CsvExportDefaultFields from '../constants/CsvExportDefaultFields';
import CsvExportGroupFieldName from '../constants/CsvExportGroupFieldName';
import CsvExportHiddenFields from '../constants/CsvExportHiddenFields';
import useConstituentExport from '../hooks/useConstituentExport';
import type CsvExportFormValues from '../types/CsvExportFormValues';
import BasicProfileFields from './BasicProfileFields';
import CsvExportSaveButton from './CsvExportSaveButton';
import HouseholdInformationFields from './HouseholdInformationFields';

const FIELD_NAMES = [
  CsvExportGroupFieldName.BASIC_PROFILE,
  CsvExportGroupFieldName.HOUSEHOLD_INFORMATION,
  ...CsvExportDefaultFields.map(({ fieldName }) => fieldName),
];

const NON_HOUSEHOLD_ENHANCEMENTS_EXCLUDED_FIELDS = [
  ConstituentExportFieldName.PREFERRED_HOUSEHOLD_GREETING,
  ConstituentExportFieldName.SPOUSE_PARTNER_NAME,
  ConstituentExportFieldName.SPOUSE_PARTNER_EMAIL,
];

function ConstituentExportCSVDialog({
  filters,
  onClose,
  open,
  totalConstituents = 0,
}: {
  filters: ConstituentExportFilters;
  onClose: () => void;
  open: boolean;
  totalConstituents?: number;
}) {
  const hasHouseholdEnhancements = useFeature(
    FeatureFlags.HOUSEHOLD_ENHANCEMENTS,
  );
  const apiFields = React.useMemo(() => {
    if (!hasHouseholdEnhancements) {
      return CsvExportDefaultFields.filter(
        ({ fieldName }) =>
          !NON_HOUSEHOLD_ENHANCEMENTS_EXCLUDED_FIELDS.includes(fieldName),
      );
    }

    return CsvExportDefaultFields;
  }, [hasHouseholdEnhancements]);

  const defaultValues = React.useMemo(() => {
    return {
      ...apiFields.reduce((memo, { fieldName }) => {
        return {
          ...memo,
          [fieldName]: true,
        };
      }, {}),
      /**
       * The hidden fields are true by default, with no way to
       * set them to false
       */
      ...CsvExportHiddenFields.reduce((memo, { fieldName }) => {
        return {
          ...memo,
          [fieldName]: true,
        };
      }, {}),
      [ConstituentExportFieldName.CUSTOM_FIELDS]: true,
      [CsvExportGroupFieldName.BASIC_PROFILE]: true,
      [CsvExportGroupFieldName.HOUSEHOLD_INFORMATION]: true,
    };
  }, [apiFields]) as CsvExportFormValues;

  const formMethods = useForm<CsvExportFormValues>({
    mode: 'onBlur',
    defaultValues,
  });

  const isMobile = useMobileView();
  const { user } = useCurrentUser();

  const { apiResponse, createExport, isExporting, resetApiResponse } =
    useConstituentExport();

  const handleSubmit = React.useCallback(
    (formData: CsvExportFormValues) => {
      createExport({
        fields: apiFields.map((field) => ({
          ...field,
          value: formData[field.fieldName],
        })),
        filters,
      });
    },
    [apiFields, createExport, filters],
  );

  if (apiResponse.error || apiResponse.res) {
    return (
      <CustomDialog
        actionButtonLabel={translate('EXPORT')}
        className="min-w-full"
        data-testid="constituent-export-csv"
        dialogHeader={translate('CONSTITUENT_EXPORT_LIST')}
        fullWidth
        onClose={onClose}
        open={open}
        showDialogActions={false}
        TransitionProps={{
          onExited: () => resetApiResponse(),
        }}
      >
        <div
          className="flex flex-col items-center"
          data-testid={`constituent-export-dialog-api-${
            apiResponse.error ? 'error' : 'success'
          }-feedback`}
        >
          <div className="pb-3 font-semibold md:pb-4">
            {apiResponse.error
              ? translate('EXPORT_ERROR')
              : translate('EXPORT_FILE_GENERATED')}
          </div>
          <div className="text-center text-base font-normal">
            {apiResponse.error
              ? translate('EXPORT_ERROR_MESSAGE')
              : translate('EXPORT_SUCCESS_MESSAGE', {
                  email: user.email,
                })}
          </div>
          {!apiResponse.error && (
            <div className="pt-3 md:pt-4">
              <Link href={AuthRoutes.USER_DOWNLOADS}>
                {translate('EXPORT_SUCCESS_DOWNLOADS_LINK')}
              </Link>
            </div>
          )}
        </div>
      </CustomDialog>
    );
  }

  return (
    <Dialog
      data-testid="constituent-export-csv-dialog"
      data-theme="v2"
      fullWidth={isMobile}
      onClose={onClose}
      open={open}
    >
      <FormProvider {...formMethods}>
        <DialogHeader
          headerText={translate('CONSTITUENT_EXPORT_LIST')}
          onClose={onClose}
        />
        <DialogContent dividers className="hide-scrollbar">
          <form
            id="constituent-export-csv-dialog-form"
            onSubmit={formMethods.handleSubmit(handleSubmit)}
          >
            <div className="flex flex-wrap justify-between pb-4 md:pb-6">
              <span className="mr-3 inline-block type-body1">
                {totalConstituents} {translate('CONSTITUENTS')}
              </span>
              {hasHouseholdEnhancements && (
                <SelectAllCheckboxesOrNone fieldNames={FIELD_NAMES} />
              )}
            </div>

            {!hasHouseholdEnhancements && (
              <div className="flex justify-between pb-4 md:pb-6">
                <div>{translate('CONSTITUENT_FIELDS_INCLUDE')}</div>
                <SelectAllCheckboxesOrNone fieldNames={FIELD_NAMES} />
              </div>
            )}
            {hasHouseholdEnhancements ? (
              <div className="grid grid-flow-row gap-1">
                <BasicProfileFields />
                <HouseholdInformationFields />
                <CsvExportCustomFields />
              </div>
            ) : (
              <>
                <BasicProfileFields />
                <hr className="mx-auto my-4 w-56 border-grey-600" />
                <CsvExportCustomFields />
              </>
            )}
          </form>
        </DialogContent>
        <DialogActions>
          <CsvExportSaveButton isSubmitting={isExporting} />
          <DialogCancelButton onClick={onClose} />
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

export default ConstituentExportCSVDialog;
